import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Button, Icon, Intent } from '@blueprintjs/core';
import moment from 'moment';
import qs from 'qs';
import Toast from 'helpers/toaster';
import * as DataList from 'components/DataList';
import { photoUrls } from 'helpers/equipmentPhotos';
import AddEquipmentModal from 'components/Equipment/AddEquipmentModal';
import AddEquipmentPartModal, { PART_TYPES } from 'components/Equipment/AddEquipmentPartModal';
import EquipmentPhotos from 'components/Equipment/EquipmentPhotos';
import EquipmentWorkOrders from 'components/Equipment/EquipmentWorkOrders';

export const equipmentName = (attributes) => attributes.kind || [attributes.make, attributes.eq_model].filter(Boolean).join(' ') || 'Equipment';

const partType = (value) => {
  const found = PART_TYPES.find((type) => type.value === value);
  return found ? found.label : value;
};

// An equipment's page (pages/equipments/show.js), in the client page's style
// (client-page.scss, user-page.scss cards): a header with what it is, whose it
// is and where; then its data plate (make, model, serial, the one bold thing
// on the page, drawn like the metal plate on the unit), its parts and photos;
// then the work orders it was serviced on. Styles: equipment-page.scss (eqp-).
@inject('equipmentStore', 'viewStore') @observer
class EquipmentPage extends Component {
  @observable isEditOpen = false;
  @observable partPanel = { isOpen: false, part: null };
  @observable deletingPart = null; // the part waiting on the delete prompt

  // Edit in the New equipment panel, filled with a copy of this equipment.
  openEdit = () => {
    this.props.equipmentStore.startEditing(this.props.equipment);
    this.isEditOpen = true;
  };

  copySerial = () => {
    const serial = this.props.equipment.attributes.serial_number;
    const clipboard = typeof navigator !== 'undefined' && navigator.clipboard;
    if (!clipboard || !clipboard.writeText) {
      Toast.show({ message: "Couldn't copy. Select the serial number and copy it instead.", intent: Intent.WARNING });
      return;
    }
    clipboard.writeText(serial)
      .then(() => { Toast.show({ message: 'Serial number copied.', intent: Intent.SUCCESS }); })
      .catch(() => { Toast.show({ message: "Couldn't copy. Select the serial number and copy it instead.", intent: Intent.WARNING }); });
  };

  confirmDeletePart = () => {
    const part = this.deletingPart;
    this.deletingPart = null;
    this.props.equipmentStore.deletePart(part)
      .then(() => { Toast.show({ message: `${part.name || 'Part'} deleted.`, intent: Intent.SUCCESS }); })
      .catch(() => { Toast.show({ message: "Couldn't delete the part. Try again.", intent: Intent.DANGER }); });
  };

  renderHeader() {
    const { equipment, viewStore } = this.props;
    const a = equipment.attributes;
    const photo = (a.images || []).map(photoUrls).find(Boolean);
    const client = a.client;
    const address = a.location && a.location.full_address;
    const retired = a.active === false;
    return (
      <header className="cp-head eqp-head" aria-label="Equipment">
        {photo ? (
          <img className="cp-avatar eqp-avatar" src={photo.thumbnail} alt="" />
        ) : (
          <span className="cp-avatar eqp-avatar" aria-hidden="true"><Icon icon="wrench" iconSize={20} /></span>
        )}
        <div className="cp-head__who">
          <div className="eqp-title">
            <h1 className="cp-head__name">{equipmentName(a)}</h1>
            {retired && <DataList.Pill tone="grey">Retired</DataList.Pill>}
          </div>
          <div className="cp-head__meta">
            {client && client.name && (
              <a href={`/dashboard/clients/${client.id}?${qs.stringify(viewStore.clientShowSearch, { encode: false })}`}>
                <Icon icon="person" iconSize={12} />{client.name}
              </a>
            )}
            {address && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`} target="_blank" rel="noopener noreferrer">
                <Icon icon="map-marker" iconSize={12} />{address}
              </a>
            )}
            {a.eq_location && <span><Icon icon="home" iconSize={12} />{a.eq_location}</span>}
          </div>
        </div>
        <Button className="cp-head__edit" icon="edit" text="Edit details" onClick={this.openEdit} />
      </header>
    );
  }

  // The bold element: make, model and serial set like the unit's rating plate.
  renderPlate() {
    const a = this.props.equipment.attributes;
    return (
      <section className="up-card eqp-plate-card" aria-labelledby="eqp-plate-title">
        <header className="up-card__head">
          <h2 className="up-card__title" id="eqp-plate-title">Data plate</h2>
          {a.created_at && <span className="up-card__note">Added {moment(a.created_at).format('MMM D, YYYY')}</span>}
        </header>
        <div className="eqp-plate">
          <span className="eqp-rivet eqp-rivet--tl" aria-hidden="true" />
          <span className="eqp-rivet eqp-rivet--tr" aria-hidden="true" />
          <span className="eqp-rivet eqp-rivet--bl" aria-hidden="true" />
          <span className="eqp-rivet eqp-rivet--br" aria-hidden="true" />
          <p className="eqp-plate__make">{a.make || <span className="eqp-plate__blank">No make</span>}</p>
          <dl className="eqp-plate__rows">
            <div>
              <dt>Model</dt>
              <dd>{a.eq_model || <span className="eqp-plate__blank">None</span>}</dd>
            </div>
            <div>
              <dt>Serial no.</dt>
              <dd>
                {a.serial_number || <span className="eqp-plate__blank">None</span>}
                {a.serial_number && (
                  <button type="button" className="up-icon-button eqp-copy" aria-label="Copy serial number" title="Copy serial number" onClick={this.copySerial}>
                    <Icon icon="duplicate" iconSize={12} />
                  </button>
                )}
              </dd>
            </div>
          </dl>
        </div>
        {a.description
          ? <p className="eqp-description">{a.description}</p>
          : <p className="up-empty">No description. Add one in Edit details.</p>}
      </section>
    );
  }

  renderParts() {
    const { equipmentStore } = this.props;
    const parts = this.props.equipment.attributes.equipment_parts || [];
    return (
      <section className="up-card eqp-parts" aria-labelledby="eqp-parts-title">
        <header className="up-card__head">
          <div className="eqp-card-title">
            <h2 className="up-card__title" id="eqp-parts-title">Parts</h2>
            {parts.length > 0 && <span className="up-card__note">{parts.length} {parts.length === 1 ? 'part' : 'parts'}</span>}
          </div>
          <Button className="up-button" icon="add" text="Add part" onClick={() => { this.partPanel = { isOpen: true, part: null }; }} />
        </header>
        {parts.length === 0 ? (
          <p className="up-empty">No parts listed. Add the filters and belts it takes, so the right ones go on the truck.</p>
        ) : (
          <ul className="eqp-part-list">
            {parts.map((part) => (
              <li key={part.id} className={`eqp-part${equipmentStore.deletingPartId === part.id ? ' is-busy' : ''}`}>
                <div className="eqp-part__main">
                  <div className="eqp-part__name">
                    <strong>{part.name || 'Part'}</strong>
                    {part.eq_type && <span className="eqp-part__type">{partType(part.eq_type)}</span>}
                    {part.eq_size && <span className="eqp-part__size">{part.eq_size}</span>}
                  </div>
                  {part.description && <p className="eqp-part__description">{part.description}</p>}
                </div>
                <div className="eqp-part__actions">
                  <button type="button" className="up-icon-button" aria-label={`Edit ${part.name || 'part'}`} title="Edit part" onClick={() => { this.partPanel = { isOpen: true, part }; }}>
                    <Icon icon="edit" iconSize={14} />
                  </button>
                  <button type="button" className="up-icon-button up-icon-button--danger" aria-label={`Delete ${part.name || 'part'}`} title="Delete part" disabled={equipmentStore.deletingPartId === part.id} onClick={() => { this.deletingPart = part; }}>
                    <Icon icon="trash" iconSize={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  render() {
    const { equipment } = this.props;
    return (
      <div className="cp up eqp">
        {this.renderHeader()}

        <div className="eqp-cards">
          {this.renderPlate()}
          {this.renderParts()}
          <EquipmentPhotos equipment={equipment} />
        </div>

        <section className="eqp-work-orders" aria-label="Work orders">
          <EquipmentWorkOrders equipment={equipment} />
        </section>

        <AddEquipmentModal isOpen={this.isEditOpen} onClose={() => { this.isEditOpen = false; }} />
        <AddEquipmentPartModal
          equipment={equipment}
          part={this.partPanel.part}
          isOpen={this.partPanel.isOpen}
          onClose={() => { this.partPanel = { isOpen: false, part: this.partPanel.part }; }}
        />
        <Alert
          isOpen={Boolean(this.deletingPart)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Delete part"
          onCancel={() => { this.deletingPart = null; }}
          onConfirm={this.confirmDeletePart}
        >
          <strong>Delete {(this.deletingPart && this.deletingPart.name) || 'this part'}?</strong>
          <p>It’s removed from this equipment’s parts list right away.</p>
        </Alert>
      </div>
    );
  }
}

// Shown while the equipment loads: the page's outline, shimmering.
export const EquipmentPageSkeleton = () => (
  <div className="cp up eqp cp--loading" role="status" aria-label="Loading equipment">
    <div className="cp-head" aria-hidden="true">
      <span className="dl-bone cp-avatar" />
      <div className="cp-head__who">
        <span className="dl-bone" style={{ width: 220, height: 20 }} />
        <span className="dl-bone" style={{ width: 320, height: 12, marginTop: 10 }} />
      </div>
      <span className="dl-bone" style={{ width: 120, height: 36, borderRadius: 8 }} />
    </div>
    <div className="eqp-cards" aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <div key={index} className="up-card">
          <span className="dl-bone" style={{ width: 120, height: 16 }} />
          <span className="dl-bone" style={{ width: '100%', height: 110 }} />
        </div>
      ))}
    </div>
  </div>
);

export default EquipmentPage;
